import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ShieldCheck, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader } from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { useBooking } from "../context/BookingContext";
import api, { formatApiError } from "../lib/api";
import { IMG } from "../lib/media";

const CONSULT_SERVICE = "New Client - Consultation";

export default function FirstVisit() {
  const { openBooking } = useBooking();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/first-visit/lead", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: "First Visit — requested a complimentary new-client consultation",
        service_interest: CONSULT_SERVICE,
      });
    } catch (err) {
      // Don't block booking if lead save hiccups
      console.warn(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
    const [firstName, ...rest] = form.name.trim().split(" ");
    openBooking(CONSULT_SERVICE, {
      firstName,
      lastName: rest.join(" "),
      email: form.email.trim(),
      phoneNumber: form.phone.trim(),
    });
  };

  return (
    <div data-testid="first-visit-page">
      <Seo
        title="First Time Visit Offer"
        description="New to Iridescent Medical & Wellness Spa? Book your complimentary new-client consultation with our aesthetic experts in Clinton Township, MI."
        path="/first-visit"
      />
      <PageHeader
        overline="New Client Offer"
        title="Your complimentary consultation"
        subtitle="A one-on-one consultation with our aesthetic experts to map your personalized treatment plan — the perfect introduction to Iridescent."
      />
      <div className="max-w-5xl mx-auto px-6 pb-24 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="rounded-[1.75rem] overflow-hidden aspect-[4/5] shadow-xl shadow-blue-900/10">
            <img src={IMG.facial} alt="Consultation at Iridescent Medical Spa" className="w-full h-full object-cover" />
          </div>
          <div className="mt-6 space-y-3">
            {[
              [Sparkles, "Personalized skin & treatment assessment"],
              [Check, "One-on-one time with our aesthetic experts"],
              [ShieldCheck, "Complimentary & no obligation — new clients"],
            ].map(([Icon, text]) => (
              <div key={text} className="flex items-start gap-3 text-sm text-[#5C6664]">
                <Icon className="h-4 w-4 mt-0.5 text-iri-mid shrink-0" /> {text}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-end gap-2 pb-5 border-b border-slate-100">
            <span className="font-heading text-5xl font-light text-ink" data-testid="first-visit-price">Free</span>
            <span className="mb-1.5 text-sm text-[#8A9492]">complimentary · new clients</span>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="first-visit-form">
            <p className="text-sm text-[#5C6664]">Tell us a little about you, then pick your time — it takes under a minute.</p>
            <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="first-visit-name" className="rounded-lg h-11" />
            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="first-visit-email" className="rounded-lg h-11" />
            <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="first-visit-phone" className="rounded-lg h-11" />
            <Button type="submit" disabled={busy} data-testid="first-visit-continue" className="w-full iri-gradient text-ink rounded-full h-12 font-medium hover:opacity-90 hover:scale-[1.02] transition-all">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Calendar className="h-4 w-4 mr-2" /> Book my consultation</>)}
            </Button>
            <p className="text-xs text-[#8A9492] text-center">You'll choose your provider and time in the next step. No payment required for your consultation.</p>
          </form>

          <p className="text-xs text-[#8A9492] text-center mt-4 pt-4 border-t border-slate-100">
            Want to earn rewards on every visit?{" "}
            <Link to="/register" className="text-ink underline">Create a free member account</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
